import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BundleFlightData } from '../../modules/FlightDetails/flightSlice';
import { OptionalFilter } from '../../components/SideFilters/interface';
import {
  FlightDynamicAsyncState,
  SupplierError,
  AsyncSearchStatus,
} from './flightAsyncTypes';
import {
  mergeFlightsByTsTypePriority,
  getPriorityFlightCountDelta,
  sortFlightsByPriorityAndPrice,
} from './flightAsyncUtils';

const initialDirectionState = {
  status: AsyncSearchStatus.Idle,
  flights: [],
  priorityFlightCount: 0,
  returnedSuppliers: 0,
  supplierErrors: [],
  optionalFilters: [],
  error: null,
  searchStartTime: null,
  searchEndTime: null,
  totalCount: 0,
  successCount: 0,
  errorCount: 0,
};

const initialState: FlightDynamicAsyncState = {
  outbound: { ...initialDirectionState },
  return: { ...initialDirectionState },
};

export const flightDynamicAsyncSlice = createSlice({
  name: 'flightDynamicAsync',
  initialState,
  reducers: {
    // Outbound flight actions
    startSSESearchOutbound: (state) => {
      state.outbound = {
        ...initialDirectionState,
        status: AsyncSearchStatus.LoadingAll,
        searchStartTime: Date.now(),
      };
    },

    receiveFirstChunkOutbound: (
      state,
      action: PayloadAction<{
        flights: BundleFlightData[];
        ts_id: number;
        ts_type?: string;
      }>
    ) => {
      state.outbound.status = AsyncSearchStatus.FirstChunkReceived;
      state.outbound.priorityFlightCount += getPriorityFlightCountDelta(
        action.payload.flights.length,
        action.payload.ts_type
      );
      mergeFlightsByTsTypePriority(
        state.outbound.flights,
        action.payload.flights,
        action.payload.ts_type
      );
      state.outbound.returnedSuppliers += 1;
    },

    receiveChunkOutbound: (
      state,
      action: PayloadAction<{
        flights: BundleFlightData[];
        ts_id: number;
        ts_type?: string;
      }>
    ) => {
      if (
        state.outbound.status === AsyncSearchStatus.FirstChunkReceived ||
        state.outbound.status === AsyncSearchStatus.LoadingAll
      ) {
        state.outbound.status = AsyncSearchStatus.Streaming;
      }
      state.outbound.priorityFlightCount += getPriorityFlightCountDelta(
        action.payload.flights.length,
        action.payload.ts_type
      );
      mergeFlightsByTsTypePriority(
        state.outbound.flights,
        action.payload.flights,
        action.payload.ts_type
      );
      state.outbound.returnedSuppliers += 1;
    },

    receiveSupplierErrorOutbound: (state, action: PayloadAction<SupplierError>) => {
      state.outbound.supplierErrors.push(action.payload);
      state.outbound.returnedSuppliers += 1;
    },

    updateOptionalFiltersOutbound: (state, action: PayloadAction<OptionalFilter[]>) => {
      state.outbound.optionalFilters = action.payload;
    },

    updateProgressOutbound: (
      state,
      action: PayloadAction<{
        totalCount: number;
        successCount: number;
        errorCount: number;
      }>
    ) => {
      state.outbound.totalCount = action.payload.totalCount;
      state.outbound.successCount = action.payload.successCount;
      state.outbound.errorCount = action.payload.errorCount;
    },

    completeSSESearchOutbound: (state) => {
      sortFlightsByPriorityAndPrice(
        state.outbound.flights,
        state.outbound.priorityFlightCount
      );
      state.outbound.status = AsyncSearchStatus.Complete;
      state.outbound.searchEndTime = Date.now();
    },

    errorSSESearchOutbound: (state, action: PayloadAction<string>) => {
      state.outbound.status = AsyncSearchStatus.Error;
      state.outbound.error = action.payload;
      state.outbound.searchEndTime = Date.now();
    },

    // Return flight actions
    startSSESearchReturn: (state) => {
      state.return = {
        ...initialDirectionState,
        status: AsyncSearchStatus.LoadingAll,
        searchStartTime: Date.now(),
      };
    },

    receiveFirstChunkReturn: (
      state,
      action: PayloadAction<{
        flights: BundleFlightData[];
        ts_id: number;
        ts_type?: string;
      }>
    ) => {
      state.return.status = AsyncSearchStatus.FirstChunkReceived;
      state.return.priorityFlightCount += getPriorityFlightCountDelta(
        action.payload.flights.length,
        action.payload.ts_type
      );
      mergeFlightsByTsTypePriority(
        state.return.flights,
        action.payload.flights,
        action.payload.ts_type
      );
      state.return.returnedSuppliers += 1;
    },

    receiveChunkReturn: (
      state,
      action: PayloadAction<{
        flights: BundleFlightData[];
        ts_id: number;
        ts_type?: string;
      }>
    ) => {
      if (
        state.return.status === AsyncSearchStatus.FirstChunkReceived ||
        state.return.status === AsyncSearchStatus.LoadingAll
      ) {
        state.return.status = AsyncSearchStatus.Streaming;
      }
      state.return.priorityFlightCount += getPriorityFlightCountDelta(
        action.payload.flights.length,
        action.payload.ts_type
      );
      mergeFlightsByTsTypePriority(
        state.return.flights,
        action.payload.flights,
        action.payload.ts_type
      );
      state.return.returnedSuppliers += 1;
    },

    receiveSupplierErrorReturn: (state, action: PayloadAction<SupplierError>) => {
      state.return.supplierErrors.push(action.payload);
      state.return.returnedSuppliers += 1;
    },

    updateOptionalFiltersReturn: (state, action: PayloadAction<OptionalFilter[]>) => {
      state.return.optionalFilters = action.payload;
    },

    updateProgressReturn: (
      state,
      action: PayloadAction<{
        totalCount: number;
        successCount: number;
        errorCount: number;
      }>
    ) => {
      state.return.totalCount = action.payload.totalCount;
      state.return.successCount = action.payload.successCount;
      state.return.errorCount = action.payload.errorCount;
    },

    completeSSESearchReturn: (state) => {
      sortFlightsByPriorityAndPrice(
        state.return.flights,
        state.return.priorityFlightCount
      );
      state.return.status = AsyncSearchStatus.Complete;
      state.return.searchEndTime = Date.now();
    },

    errorSSESearchReturn: (state, action: PayloadAction<string>) => {
      state.return.status = AsyncSearchStatus.Error;
      state.return.error = action.payload;
      state.return.searchEndTime = Date.now();
    },

    // Reset both directions
    resetDynamicAsyncSearch: (state) => {
      return initialState;
    },
  },
});

export const {
  startSSESearchOutbound,
  receiveFirstChunkOutbound,
  receiveChunkOutbound,
  receiveSupplierErrorOutbound,
  updateOptionalFiltersOutbound,
  updateProgressOutbound,
  completeSSESearchOutbound,
  errorSSESearchOutbound,
  startSSESearchReturn,
  receiveFirstChunkReturn,
  receiveChunkReturn,
  receiveSupplierErrorReturn,
  updateOptionalFiltersReturn,
  updateProgressReturn,
  completeSSESearchReturn,
  errorSSESearchReturn,
  resetDynamicAsyncSearch,
} = flightDynamicAsyncSlice.actions;

export default flightDynamicAsyncSlice.reducer;
